import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const BarcodeGenerationForm = ({ onGenerate, loading }) => {
  const { currentTenant } = useAuth();
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState('');
  const [barcodeType, setBarcodeType] = useState('qr_code');
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMembers();
  }, [currentTenant?.id]);

  const fetchMembers = async () => {
    setLoadingMembers(true);
    try {
      const { data, error } = await supabase
        ?.from('tenant_members')
        ?.select(`
          *,
          user_profiles!tenant_members_user_id_fkey(id, full_name, email)
        `)
        ?.eq('tenant_id', currentTenant?.id)
        ?.eq('is_active', true)
        ?.order('user_profiles(full_name)');

      if (error) throw error;

      // Filter out members who already have active barcodes
      const { data: existingBarcodes } = await supabase
        ?.from('member_barcodes')
        ?.select('user_id')
        ?.eq('tenant_id', currentTenant?.id)
        ?.eq('is_active', true);

      const existingUserIds = new Set(existingBarcodes?.map(b => b?.user_id) || []);
      const availableMembers = data?.filter(m => !existingUserIds?.has(m?.user_profiles?.id)) || [];

      setMembers(availableMembers);
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setGenerating(true);
    setSuccess('');
    setError('');

    try {
      await onGenerate(selectedMember, barcodeType);
      setSuccess('Barcode generated successfully!');
      setSelectedMember('');
      fetchMembers(); // Refresh list
    } catch (error) {
      setError('Failed to generate barcode. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const barcodeTypeOptions = [
    { value: 'qr_code', label: 'QR Code', description: 'Standard QR code format' },
    { value: 'barcode', label: 'Barcode', description: 'Traditional barcode format' },
    { value: 'nfc', label: 'NFC', description: 'NFC-enabled format' }
  ];

  return (
    <div className="bg-card rounded-lg p-6 border border-border">
      <h2 className="text-xl font-bold mb-6">Generate Member Barcode</h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Member Selection */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Select Member
          </label>
          <Select
            value={selectedMember}
            onChange={(value) => setSelectedMember(value)}
            options={members?.map(member => ({
              value: member?.user_profiles?.id,
              label: member?.user_profiles?.full_name,
              description: member?.user_profiles?.email
            }))}
            placeholder="Choose a member"
            searchable
            loading={loadingMembers}
            disabled={generating}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Only members without active barcodes are shown
          </p>
        </div>

        {/* Barcode Type */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Barcode Type
          </label>
          <Select
            value={barcodeType}
            onChange={(value) => setBarcodeType(value)}
            options={barcodeTypeOptions}
            disabled={generating}
          />
        </div>

        {/* Success Message */}
        {success && (
          <div className="p-3 bg-success/10 border border-success rounded-lg">
            <p className="text-sm text-success flex items-center gap-2">
              <Icon name="CheckCircle" size={16} />
              {success}
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-destructive/10 border border-destructive rounded-lg">
            <p className="text-sm text-destructive flex items-center gap-2">
              <Icon name="AlertCircle" size={16} />
              {error}
            </p>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={!selectedMember || generating}
          loading={generating}
          iconName="Plus"
          className="w-full"
        >
          Generate Barcode
        </Button>
      </form>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-muted rounded-lg">
        <h3 className="font-semibold mb-2 flex items-center gap-2">
          <Icon name="Info" size={18} />
          Barcode Information
        </h3>
        <ul className="text-sm text-muted-foreground space-y-1 ml-6 list-disc">
          <li>Each member can have one active barcode at a time</li>
          <li>Barcodes are unique and cannot be duplicated</li>
          <li>Members can regenerate barcodes if lost or compromised</li>
          <li>QR codes work best with mobile devices</li>
        </ul>
      </div>
    </div>
  );
};

export default BarcodeGenerationForm;